import { useEffect, useRef, useState, type MouseEvent, type ReactElement } from "react";
import { SPACING } from "odyssey-shared";
import { Menu, logical, useCentred, type GlyphName, type MenuEntry } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import { DEFAULT_BINDINGS } from "../controls/bindings.ts";
import { codeLabel } from "../controls/layout.ts";
import { useKeyLabel } from "../controls/use-key.ts";
import { FeedbackWindow } from "../feedback/form.tsx";
import type { FeedbackDraft, SendOutcome } from "../feedback/send.ts";
import { accessSentence } from "../feedback/words.ts";
import { MENU_KEY } from "../hud/slots.ts";
import { lockNote, lockWhy } from "./leave-words.ts";
import { type LookScreenPage } from "./look-screen.ts";
import { LookWindow } from "./look-window.tsx";
import { Wordmark } from "../loading-screen.tsx";
import type { SettingsPage } from "./settings-page.ts";
import { SettingsWindow } from "./settings-window.tsx";
import { everywhereButton, gameMenuItems, type GameMenuEvent, type GameMenuItem, type GameMenuState } from "./state.ts";
import { menuItemName, menuLabel } from "./wording.ts";

export type { AudioSetting, AutoLoot, BarKeys, BarLock, BarPaging, BarReset, ChatSizeToggle, ChatTextToggle, CompassPlaces, EmotesOthers, FrameLimitSetting, HealthNumbers, IdleMarks, MotionSetting, NameplateSetting, PartyInvitesSetting, PerformanceToggle, RenderSetting, SettingsPage, ShowCoordinates, ZoomSetting } from "./settings-page.ts";

/**
 * The game menu (P1.9-T14; ADR-061 and its amendments; the brief's game menu, the design export a visual
 * reference for where it sits): over a scrim, the game's wordmark in the title face, which ADR-061 gives the game
 * menu, over the kit's `Menu` in the middle of the window on whole logical pixels; and Settings over the menu
 * (`settings-window.tsx`, P1.9-T44). As the design draws the menu (ADR-061's amendment "the game menu, character
 * select, loading and confirm dialog match the design"), each item leads with its glyph, Resume carries the Esc
 * key glyph, Inventory (P2.8-T11, the Inventory window's pointer and touch path, which the design proposes as a menu
 * tile) carries its key's, and after a divider Change character (P1.9-T62), a plain entry, stands above Log out, the menu's one
 * entry in the danger colour; both are an account session's. Log out everywhere is Settings' Account section's,
 * for an account session, until P1.4-T32 builds the Account pane. Feedback (ADR-081's amendment of 2026-10-05)
 * stands after Inventory on a page that has somewhere to send a report, with the chat glyph; the design's menu has
 * no such entry, so it is drawn in the menu's own style. Its form opens over the menu as Settings does
 * (`feedback/form.tsx`). While the account's feedback is paused or turned off the entry is disabled and a sentence
 * under the menu, as wide as the menu and a live region, says so and, for a pause, when it ends. Change look
 * (ADR-094's amendment of 2026-10-06, B11) stands after Inventory on an account's page that offers the temporary
 * screen, with the small standing figure; the design's menu has no such entry either. Its window opens over the menu
 * (`look-window.tsx`), and while the page's freeze notice stands the entry is disabled and a sentence under the menu
 * says why. While the character is in combat Change character and Log out are refused, each naming the wait at
 * its right as the design draws it (the kit's `why`), and the design's sentence under the menu says so too, both
 * with the seconds left, counted down as they show (P2.3-T4, ADR-084's amendment of 2026-10-13).
 *
 * The view draws a `GameMenuState` and reports what the player does as `GameMenuEvent`s; `mount.tsx` moves
 * the state and runs what an item asks for. The menu holds the keyboard as the router's topmost layer while
 * it shows (the kit's `Menu`), and Settings its own layer above it (the kit's `ModalWindow`), so Escape closes
 * Settings first, then the menu, and no key reaches the game behind either. A press beside the menu's items
 * (on the scrim, the wordmark or the menu's frame) keeps focus in the menu, so the arrow keys and Enter still
 * reach it after a click beside an item. The component imports no stylesheet, so it renders to static
 * markup under `bun test`; `mount.tsx` imports the sheets.
 */

/**
 * How the menu lays out, in logical pixels, from the spacing steps: the gap between the wordmark and the menu
 * (6), and the menu's least width, the design's.
 */
export const GAME_MENU_METRICS = { wordmarkGap: SPACING[4], menuWidth: 120 } as const;

/**
 * How each item of the menu is drawn, as the design draws it: the glyph it leads with, the key it names, whether it
 * is in the danger colour and whether a divider stands before it.
 */
const ITEM_LOOKS: Readonly<Record<Exclude<GameMenuItem, "everywhere">, { readonly glyph: GlyphName; readonly hint?: string; readonly danger?: boolean; readonly divided?: boolean }>> = {
  resume: { glyph: "play", hint: MENU_KEY },
  settings: { glyph: "sliders" },
  inventory: { glyph: "briefcase-small" },
  character: { glyph: "user-small" },
  emotes: { glyph: "chat" },
  look: { glyph: "human-small" },
  feedback: { glyph: "chat" },
  change: { glyph: "user-small", divided: true },
  logout: { glyph: "logout", danger: true },
};

/**
 * The menu's entries as the kit's menu takes them: each item's words and look, and whether it is busy or disabled.
 *
 * @param state The menu's state.
 * @param inventoryKey The label of the key Inventory is bound to (`controls/`), which its item names; I unless given.
 * @param emotesKey The label of the key Emotes is bound to, which its item names; H unless given.
 * @param now The page's clock now, against which a fight's lock on Change character and Log out is worded.
 * @param characterKey The label of the key Character is bound to, shown beside the Character item.
 * @returns The entries, in order.
 */
export function gameMenuEntries(state: GameMenuState, inventoryKey: string = codeLabel(DEFAULT_BINDINGS.inventory), emotesKey: string = codeLabel(DEFAULT_BINDINGS.emotes), now: number = Date.now(), characterKey: string = codeLabel(DEFAULT_BINDINGS.characterSheet)): readonly MenuEntry<GameMenuItem>[] {
  return gameMenuItems(state).map((entry) => {
    const look = entry.item === "everywhere" ? {} : ITEM_LOOKS[entry.item];
    return { value: entry.item, label: menuItemName(entry.item), disabled: entry.disabled, busy: entry.busy, ...look, ...(entry.item === "inventory" ? { hint: inventoryKey } : {}), ...(entry.item === "character" ? { hint: characterKey } : {}), ...(entry.item === "emotes" ? { hint: emotesKey } : {}), ...((entry.item === "change" || entry.item === "logout") && state.lock !== null && state.leaving === null ? { why: lockWhy(state.lock, now) } : {}) };
  });
}

/**
 * Keeps a press anywhere on the menu's screen but on an item from taking focus from the menu: on the scrim,
 * the wordmark or the menu's frame around its items. A press on something that cannot take focus leaves
 * focus nowhere, and the menu, which still holds the keyboard, would then answer no arrow key or Enter until
 * an item was clicked.
 *
 * @param event The press.
 */
function keepMenuFocus(event: MouseEvent<HTMLElement>): void {
  if (!(event.target instanceof Element) || event.target.closest("[role='menuitem']") === null) {
    event.preventDefault();
  }
}

/**
 * How often the menu reads the clock while a fight's lock counts down, in milliseconds.
 */
const LOCK_READ_MS = 250;

/**
 * The sentence for a fight's lock, drawn again as its seconds run down.
 *
 * @param until The time on the page's clock the lock ends, or null for none.
 * @param now The page's clock.
 * @returns The sentence, or null with no lock.
 */
function useLockNote(until: number | null, now: () => number): string | null {
  const [, setBeat] = useState(0);
  useEffect(() => {
    if (until === null) {
      return undefined;
    }
    const timer = setInterval(() => setBeat((beat) => beat + 1), LOCK_READ_MS);
    return () => clearInterval(timer);
  }, [until]);
  return lockNote(until, now());
}

/**
 * The open menu: the scrim, and on it the wordmark over the kit's menu, the column in the middle of the
 * window and each part in the middle of the column, all on whole logical pixels.
 *
 * @param props The `state` and `onEvent`.
 * @returns The scrim.
 */
function MenuScreen({ state, onEvent, now }: { readonly state: GameMenuState; readonly onEvent: (event: GameMenuEvent) => void; readonly now: () => number }): ReactElement {
  const scrim = useRef<HTMLDivElement>(null);
  const column = useRef<HTMLDivElement>(null);
  const wordmark = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const leaveNote = useLockNote(state.lock, now);
  const items = gameMenuEntries(state, useKeyLabel("inventory"), useKeyLabel("emotes"), now(), useKeyLabel("characterSheet"));
  const note = state.feedback === null ? null : accessSentence(state.feedback.access, now());
  const lookNote = state.look && state.frozen ? say("look.change.frozen") : null;
  const columnAt = useCentred(scrim, column, { x: true, y: true }, null);
  const wordmarkAt = useCentred(column, wordmark, { x: true, y: false }, null);
  const menuAt = useCentred(column, menu, { x: true, y: false }, null);
  return (
    <div ref={scrim} className="ot-scrim ot-game-menu" data-game-menu="screen" onMouseDown={keepMenuFocus}>
      <div ref={column} className="ot-game-menu__column" style={{ left: logical(columnAt.left), top: logical(columnAt.top), gap: logical(GAME_MENU_METRICS.wordmarkGap) }}>
        <div ref={wordmark} className="ot-game-menu__part" data-game-menu="wordmark" style={{ left: logical(wordmarkAt.left) }}>
          <Wordmark />
        </div>
        <div ref={menu} className="ot-game-menu__part" data-game-menu="menu" style={{ left: logical(menuAt.left) }}>
          <Menu label={menuLabel()} items={items} minWidth={GAME_MENU_METRICS.menuWidth} onSelect={(item) => onEvent({ type: "chosen", item })} onClose={() => onEvent({ type: "dismissed" })} />
          <p className="ot-game-menu__note" data-game-menu="feedback-note" role="status" aria-live="polite" style={note === null ? { display: "none" } : { width: logical(GAME_MENU_METRICS.menuWidth), marginTop: logical(GAME_MENU_METRICS.wordmarkGap) }}>
            {note}
          </p>
          <p className="ot-game-menu__note" data-game-menu="look-note" role="status" aria-live="polite" style={lookNote === null ? { display: "none" } : { width: logical(GAME_MENU_METRICS.menuWidth), marginTop: logical(GAME_MENU_METRICS.wordmarkGap) }}>
            {lookNote}
          </p>
          <p className="ot-game-menu__note" data-game-menu="leave-note" role="status" aria-live="polite" style={leaveNote === null ? { display: "none" } : { width: logical(GAME_MENU_METRICS.menuWidth), marginTop: logical(GAME_MENU_METRICS.wordmarkGap) }}>
            {leaveNote}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * The game menu as its state shows it: nothing while it is closed; the menu; or Settings, the Feedback form or the
 * Change look window over the menu.
 *
 * @param props The `state`; the page's settings (`zoom`, `nameplates`, `healthNumbers`, `idleMarks`, `emotesOthers`, `barKeys`, `barPaging`, `barLock`, `barReset`, `motion`,
 *   `audio`, its `performance` panel when it has one, ADR-087, and its `render` resolution when it has one,
 *   ADR-093), which Settings reads as it opens and changes
 *   on Apply; `onSend`, which sends a report from the Feedback form, on a page that has one; `now`, the page's
 *   clock, which a pause's sentence is written against; `look`, what the temporary Change look screen needs, on a
 *   page that offers it, with `lookRestUntil`, the time before which its Confirm rests; and `onEvent`, told of each
 *   thing the player does.
 * @returns The menu, or null while it is closed.
 * @throws Error when mounted outside a `KitInputProvider`, which the kit's menu and modals need.
 * @example
 * <GameMenu state={state} zoom={worldZoom} nameplates={nameplateSettings} healthNumbers={healthNumbers} idleMarks={idleMarks} emotesOthers={emotesOthers} barKeys={barKeys} barPaging={barPaging} motion={PAGE_MOTION} audio={pageSound} onEvent={dispatch} />
 */
export function GameMenu({ state, onEvent, onSend, now = Date.now, look, lookRestUntil = 0, ...page }: SettingsPage & { readonly state: GameMenuState; readonly onEvent: (event: GameMenuEvent) => void; readonly onSend?: ((draft: FeedbackDraft) => Promise<SendOutcome>) | undefined; readonly now?: () => number; readonly look?: LookScreenPage | undefined; readonly lookRestUntil?: number }): ReactElement | null {
  if (state.view === "closed") {
    return null;
  }
  return (
    <>
      <MenuScreen state={state} onEvent={onEvent} now={now} />
      {state.view === "feedback" && state.feedback !== null && onSend !== undefined ? <FeedbackWindow staff={state.feedback.kinds === "all"} onSend={onSend} onBack={() => onEvent({ type: "backed" })} /> : null}
      {state.view === "look" && look !== undefined ? <LookWindow page={look} restUntil={lookRestUntil} onBack={() => onEvent({ type: "backed" })} onDone={() => onEvent({ type: "lookChanged" })} /> : null}
      {state.view === "settings" ? <SettingsWindow page={page} account={everywhereButton(state)} onEverywhere={() => onEvent({ type: "chosen", item: "everywhere" })} onBack={() => onEvent({ type: "backed" })} /> : null}
    </>
  );
}
